import { TxRaw } from 'cosmjs-types/cosmos/tx/v1beta1/tx'
import { useAtomValue } from 'jotai'
import { useMutation } from '@tanstack/react-query'
import { CHAIN_INFO_LIST } from '@/config'
import { assert } from '@/utils'
import { calculateFeeWithMinimalDenom, convertDenomToMicroDenom } from '@/wallet-utils'
import { unstakeAmountAtom } from '../../atoms'
import { MutationParameters } from './types'
import { useSelectedDenom } from '@/contexts/denom'
import { useSelectedChain, useStrideChain } from '@/contexts/wallet'
import { stride } from 'stridejs'
import { simulate } from '@/wallet-utils'
import { useHostZoneQuery } from '../../queries'

const useSignRedeemStakeMutation = ({ setRedeemStakeRaw }: MutationParameters) => {
  const [denom] = useSelectedDenom()

  const { address: selectedAddress } = useSelectedChain()

  const { address: strideAddress, getSigningStargateClient } = useStrideChain()

  const { data: hostZone } = useHostZoneQuery()

  const amount = useAtomValue(unstakeAmountAtom)

  const handleMutation = async (): Promise<TxRaw> => {
    assert(strideAddress, 'Unable to redeem staked tokens without connecting your Stride wallet.')

    assert(selectedAddress, `Unable to redeem staked tokens without connecting your ${denom} wallet.`)

    assert(hostZone, `Host zone for ${denom} not found.`)

    const amountInMicroDenom = convertDenomToMicroDenom(amount, CHAIN_INFO_LIST[denom].stakeCurrency.coinMinimalDenom)

    const getMsgRedeemStake = () => {
      if (denom === 'TIA' && 'redemptions_enabled' in hostZone && !hostZone.redemptions_enabled) {
        return stride.staketia.MessageComposer.withTypeUrl.redeemStake({
          redeemer: strideAddress,
          stTokenAmount: amountInMicroDenom.toString(),
          receiver: selectedAddress
        })
      }

      if (denom === 'DYM') {
        return stride.stakedym.MessageComposer.withTypeUrl.redeemStake({
          redeemer: strideAddress,
          stTokenAmount: amountInMicroDenom.toString()
        })
      }

      return stride.stakeibc.MessageComposer.withTypeUrl.redeemStake({
        creator: strideAddress,
        amount: amountInMicroDenom.toString(),
        hostZone: CHAIN_INFO_LIST[denom].chainId,
        receiver: selectedAddress
      })
    }

    const msgRedeemStake = getMsgRedeemStake()

    const client = await getSigningStargateClient()

    const gas = await simulate(client, strideAddress, [msgRedeemStake], '')

    const fee = calculateFeeWithMinimalDenom('ustrd', gas)

    return await client.sign(strideAddress, [msgRedeemStake], fee, '')
  }

  const handleSuccess = (raw: TxRaw) => {
    setRedeemStakeRaw(raw)
  }

  return useMutation({
    mutationFn: handleMutation,
    onSuccess: handleSuccess
  })
}

export { useSignRedeemStakeMutation }
